import { useCallback, useLayoutEffect, useRef } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { useAuth } from "../useAuth";
import { useIsSessionCurrent } from "../useSessionSetAtom";
import { apiConfig } from "@/config/apiConfig";
import { apiSentence, markReported } from "@utils/apiFailure";
import {
    PAYMENT_HELD,
    PAYMENT_UNAVAILABLE,
    PAYMENT_UNCONFIRMED,
} from "@models/paymentMethod";
import { loginRequest } from "@/config/authConfig";

export const useApi = () => {
    const { instance, user } = useAuth();
    const { showError, showWarning } = useNotification();
    const isSessionCurrent = useIsSessionCurrent();

    // Every API function below changes identity with WHICH account is signed
    // in (none, or another), never with the account object. MSAL hands out a
    // new object for the same account at an interaction after a token renewal
    // (useAuth.test), and keying on the object made every page re-read and
    // dropped a reload held from before (PR 374 review 2 and 4). The newest
    // object is still the one sent to MSAL.
    const accountId = user?.homeAccountId ?? null;
    const userRef = useRef(user);
    useLayoutEffect(() => {
        userRef.current = user;
    }, [user]);

    const getAccessToken = useCallback(async (): Promise<string> => {
        const account = userRef.current;
        // A function made for one account never asks for another's token.
        if (!account || account.homeAccountId !== accountId) {
            throw new Error("No authenticated user found");
        }

        const request = {
            scopes: loginRequest.scopes,
            account,
        };

        try {
            const response = await instance.acquireTokenSilent(request);

            return response.accessToken;
        } catch (error) {
            console.warn("Silent token acquisition failed", error);

            if (error instanceof InteractionRequiredAuthError) {
                showWarning({
                    title: "Session expired",
                    message: "Your session expired — please sign in again.",
                });

                instance.loginRedirect(loginRequest);

                const expired = new Error("Session expired; redirecting to sign in");
                markReported(expired);
                throw expired;
            }

            throw error;
        }
    }, [instance, accountId, showWarning]);

    // The handlers below receive the already-read response body. The body of a
    // Response can only be consumed once, and callApi needs it both for the
    // toast and for ApiError.detail, so it is read there and passed down.
    const handleBadRequest = useCallback(
        (response: Response, bodyText: string): void => {
            const contentType = response.headers.get("content-type");
            const errorData = contentType?.includes("application/problem+json")
                ? parseJsonBody(bodyText)
                : undefined;

            if (errorData) {
                // Fluent Validation errors
                if (errorData.errors && typeof errorData.errors === "object") {
                    const errorMessages = Object.values(
                        errorData.errors as Record<string, unknown>,
                    )
                        .map((messages) =>
                            Array.isArray(messages)
                                ? messages.join("\n")
                                : String(messages),
                        )
                        .join("\n")
                        .replace(/'Body /g, "'");

                    showWarning({
                        title: asText(errorData.title) || "Validation Error",
                        message: errorMessages,
                    });

                    return;
                }

                showError({
                    title: asText(errorData.title) || "Bad Request",
                    message:
                        asText(errorData.message) ||
                        asText(errorData.detail) ||
                        JSON.stringify(errorData),
                });

                return;
            }

            // A plain-string 400 may arrive as a JSON string literal (the JSON
            // formatter); apiSentence unwraps it so no quotes reach the toast, and
            // leaves anything else (HTML, a bare scalar) to the raw text below.
            showError({
                title: "Bad Request",
                message:
                    apiSentence(bodyText) ?? (bodyText || response.statusText),
            });
        },
        [showError, showWarning],
    );

    const handleServerError = useCallback((): void => {
        showError({
            title: "Server Error",
            message: "Server error occurred. Please try again later.",
        });
    }, [showError]);

    const handleGenericError = useCallback(
        (response: Response, bodyText: string): void => {
            showError({
                title: `Error ${response.status}`,
                message: bodyText || response.statusText,
            });
        },
        [showError],
    );

    // A 412 is the server refusing a precondition it can NAME — the cart holds a product that
    // cannot ship to the chosen location, a variant went inactive, the featured product left the
    // category. Every PreconditionFailedResult on the API passes that sentence as the body, and
    // discarding it left the one moment a shopper is told why reading "please try again"
    // (#228 / E4-S12, Lead ruling 6). The generic copy stays as the floor for a bodyless 412.
    // Every PreconditionFailedResult hands ObjectResult a plain string, which reaches the
    // client as text/plain or as a JSON string literal; apiSentence unwraps both, and a body
    // that parses to an OBJECT is no sentence (never "[object Object]").
    const handlePreconditionFailed = useCallback(
        (bodyText: string): void => {
            showError({
                title: "Update rejected",
                message:
                    apiSentence(bodyText) ?? PRECONDITION_FAILED_FALLBACK,
            });
        },
        [showError],
    );

    const handlePaymentRequired = useCallback(
        (bodyText: string): void => {
            // The API returns a structured { error, message } body. `payment_declined` = the
            // vendor rejected the card (safe to retry); `payment_unconfirmed` = the charge outcome
            // is unknown and may have gone through, so the customer must NOT blindly retry;
            // `payment_held` = under review; `payment_unavailable` = nothing charged, the
            // gateway refused our request. paymentRequiredCopy titles each.
            const data = parseJsonBody(bodyText);

            const errorCode =
                typeof data?.error === "string" ? data.error : undefined;
            const copy = paymentRequiredCopy(errorCode);
            const message =
                typeof data?.message === "string" && data.message
                    ? data.message
                    : copy.fallback;

            showError({ title: copy.title, message });
        },
        [showError],
    );

    const handleErrorResponse = useCallback(
        (response: Response, bodyText: string): void => {
            switch (response.status) {
                case 400: {
                    handleBadRequest(response, bodyText);
                    break;
                }

                case 402: {
                    handlePaymentRequired(bodyText);
                    break;
                }

                case 412: {
                    // Only a body the API authored may become toast copy; a
                    // gateway's HTML error page is not a sentence for the shopper.
                    handlePreconditionFailed(
                        hasApiBody(response.headers.get("content-type"), bodyText)
                            ? bodyText
                            : "",
                    );
                    break;
                }

                // TODO: handle other specific status codes as needed
                default: {
                    if (response.status >= 500) {
                        handleServerError();
                    } else {
                        handleGenericError(response, bodyText);
                    }
                    break;
                }
            }
        },
        [
            handleBadRequest,
            handlePaymentRequired,
            handlePreconditionFailed,
            handleServerError,
            handleGenericError,
        ],
    );

    const callApi = useCallback<CallApi>(
        async <T = unknown>(
            endpoint: string,
            options: ApiRequestOptions = {},
        ): Promise<T> => {
            const {
                skipAuth = false,
                skipErrorNotification = false,
                contentType = "json",
                responseType = "json",
                headers = {},
                ...restOptions
            } = options;

            // The one gate for this request's toasts. A request that carried a
            // session's token and outlived that session says nothing: the
            // account it spoke for has signed out. An anonymous request
            // (skipAuth) belongs to no session and always speaks — the page-load
            // reset bumps the epoch under the public pages' first reads.
            const notify = (toast: () => void): void => {
                if (skipAuth || isSessionCurrent()) toast();
            };

            const url = endpoint.startsWith("http")
                ? endpoint
                : `${apiConfig.baseUrl}${endpoint}`;

            const requestHeaders: Record<string, string> = {
                ...(headers as Record<string, string>),
            };

            if (contentType === "json") {
                requestHeaders["Content-Type"] = "application/json";
            } else if (contentType === "multipart") {
                delete requestHeaders["Content-Type"];
                delete requestHeaders["content-type"];
            }

            if (apiConfig.apiKey) {
                requestHeaders["x-functions-key"] = apiConfig.apiKey;
            }

            if (!skipAuth) {
                const token = await getAccessToken();
                requestHeaders["Authorization"] = `Bearer ${token}`;
            }

            try {
                const response = await fetch(url, {
                    ...restOptions,
                    headers: requestHeaders,
                });

                if (response.ok) {
                    // A file download needs the body as a Blob AND a response
                    // header (Content-Disposition) — hand the caller the Response.
                    if (responseType === "response") {
                        return response as T;
                    }

                    const responseContentType =
                        response.headers.get("content-type");
                    if (
                        responseContentType &&
                        responseContentType.includes("application/json")
                    ) {
                        return response.json();
                    }

                    return response.text() as T;
                } else {
                    // Read the body exactly once: it feeds both the toast and
                    // ApiError.detail. Read even when the toast is suppressed,
                    // so callers rendering their own inline errors still get it.
                    const bodyText = await readBodyText(response);

                    // A predicate suppresses selectively: it sees the status,
                    // the machine-readable `error` code when the body carries
                    // one, whether the API authored the body at all, and the
                    // raw body, and only a `true` return suppresses. Everything
                    // it declines keeps the full-fidelity handlers below.
                    const suppressNotification =
                        typeof skipErrorNotification === "function"
                            ? skipErrorNotification(
                                response.status,
                                parseErrorCode(bodyText),
                                hasApiBody(
                                    response.headers.get("content-type"),
                                    bodyText,
                                ),
                                bodyText,
                            )
                            : skipErrorNotification;

                    const apiError = new ApiError(
                        `API request failed with status ${response.status}`,
                        response.status,
                        bodyText || undefined,
                    );

                    if (!suppressNotification) {
                        notify(() => {
                            handleErrorResponse(response, bodyText);
                            markReported(apiError);
                        });
                    }

                    throw apiError;
                }
            } catch (error) {
                // A network-level failure (no response) is judged by a
                // predicate too, with no status, code or body (#407): a read a
                // newer one has superseded stays quiet even when it never
                // reached the server. A rule about statuses declines it, so
                // its caller keeps the "check your connection" guidance. An
                // ApiError was judged above, with its status, and is not
                // asked again.
                if (!(error instanceof ApiError)) {
                    const suppressNetworkNotification =
                        typeof skipErrorNotification === "function"
                            ? skipErrorNotification(
                                undefined,
                                undefined,
                                false,
                                "",
                            )
                            : skipErrorNotification;

                    if (!suppressNetworkNotification) {
                        notify(() => {
                            showError({
                                title: "Network Error",
                                message:
                                    "Unable to reach the server. Please check your connection and try again.",
                            });
                            markReported(error);
                        });
                    }
                }

                throw error;
            }
        },
        [getAccessToken, handleErrorResponse, showError, isSessionCurrent],
    );

    return {
        callApi,
        getAccessToken,
    };
};

export interface ApiRequestOptions extends RequestInit {
    skipAuth?: boolean;
    // Suppress the automatic error toast so callers can render their own inline
    // state. `true` suppresses everything (used for background reads that
    // shouldn't surface global toasts). A predicate suppresses selectively: it
    // receives the HTTP status, the body's machine-readable `error` code (when
    // present) and `hasApiBody` — true exactly when the body is non-empty AND
    // its content type is text/plain, application/json or
    // application/problem+json, i.e. the API answered (every failure it
    // authors is one of those); false for an empty body (the Functions host
    // answering for a route it does not have) or an HTML page (a gateway) —
    // and the raw body text, for a caller that needs more of the body than its
    // code (#443's stock refusals carry `available` / `items`) — and suppresses only when it
    // returns true. A predicate declaring fewer parameters is fine.
    // A network-level failure (no response) is put to the predicate too, as
    // status undefined, no code, hasApiBody false and an empty body. A rule
    // about statuses must answer false for it to keep the network toast
    // (`status === 404` does; `status !== 500` would not).
    skipErrorNotification?:
        | boolean
        | ((
            status: number | undefined,
            errorCode: string | undefined,
            hasApiBody: boolean,
            bodyText: string,
        ) => boolean);
    contentType?: "json" | "multipart" | "none";
    // "json" (default) parses a JSON body and returns text otherwise.
    // "response" returns the raw `Response` on success, unread, for callers
    // that need the body as a Blob or a response header (file downloads,
    // E12-S5 / #240). The error path is identical for both.
    responseType?: "json" | "response";
}

// callApi's two shapes, so a `responseType: "response"` call is typed as the
// Response it actually returns and cannot be written as `callApi<Foo>(…)`;
// every other call parses JSON into T as before.
export interface CallApi {
    (
        endpoint: string,
        options: ApiRequestOptions & { responseType: "response" },
    ): Promise<Response>;
    <T = unknown>(
        endpoint: string,
        options?: ApiRequestOptions & { responseType?: "json" },
    ): Promise<T>;
}

export class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
        // Raw response body, when the server sent one. Lets a caller tell two
        // failures with the SAME status apart (e.g. the two distinct 409s on
        // the admin approve action). Not user-facing copy — callers map it to
        // their own message.
        public detail?: string,
    ) {
        super(message);
        this.name = "ApiError";
    }
}

// Reads the error body once. A body read can fail on a truncated response;
// returning "" keeps that from masking the real HTTP error as a network error.
// The failure is logged rather than swallowed silently: a test double missing
// .text() lands here too, and a quiet "" would let assertions pass against
// response.statusText instead of the body they meant to exercise.
const readBodyText = async (response: Response): Promise<string> => {
    try {
        return await response.text();
    } catch (error) {
        console.warn("Could not read the error response body", error);

        return "";
    }
};

// Parses a JSON error body. Returns undefined when the body is absent or is
// not a JSON object, so callers can fall back to the raw text.
const parseJsonBody = (
    bodyText: string,
): Record<string, unknown> | undefined => {
    if (!bodyText) {
        return undefined;
    }

    try {
        const parsed: unknown = JSON.parse(bodyText);

        return typeof parsed === "object" && parsed !== null
            ? (parsed as Record<string, unknown>)
            : undefined;
    } catch {
        return undefined;
    }
};

// Extracts the machine-readable `error` code from a structured failure body for
// the skipErrorNotification predicate. Builds on parseJsonBody — the same parse
// the handlers use — and returns undefined when the body carries no such code.
const parseErrorCode = (bodyText: string): string | undefined => {
    const data = parseJsonBody(bodyText);

    return typeof data?.error === "string" ? data.error : undefined;
};

// Whether an error body is one the API wrote — every failure the API authors
// is a non-empty body of one of these types (an ObjectResult string reaches
// the client as text/plain or a JSON string literal; validation as
// problem+json). An empty body is the Functions host answering for a route it
// does not have, an HTML body a gateway page: a caller suppressing the toast
// for a status it has contracted with the API must not do so for those.
const API_BODY_CONTENT_TYPES = [
    "application/json",
    "application/problem+json",
    "text/plain",
];

const hasApiBody = (contentType: string | null, bodyText: string): boolean =>
    bodyText.trim() !== "" &&
    contentType !== null &&
    API_BODY_CONTENT_TYPES.some((type) =>
        contentType.toLowerCase().includes(type),
    );

const asText = (value: unknown): string =>
    typeof value === "string" ? value : "";

// Shown only when the 412 carried no usable sentence of its own.
const PRECONDITION_FAILED_FALLBACK =
    "The change was rejected and nothing was updated. Please try again.";

// The 402 toast's title, chosen per code in this one place (PR 367 review,
// finding 5), and the message shown when the body carries none. Only a decline
// is titled one: payment_unavailable never judged the card, and held/unconfirmed
// must not read as "try another card". Unknown codes keep the decline framing
// they always had.
const paymentRequiredCopy = (
    code: string | undefined,
): { title: string; fallback: string } => {
    switch (code) {
        case PAYMENT_UNAVAILABLE:
            return {
                title: "Payment service unavailable",
                fallback:
                    "The payment service isn't available right now. Nothing was charged. Try again in a few minutes.",
            };
        case PAYMENT_HELD:
            return {
                title: "Payment under review",
                fallback:
                    "Your payment is being reviewed by our payment provider and hasn't completed yet. Please don't try again — contact support.",
            };
        case PAYMENT_UNCONFIRMED:
            return {
                title: "Payment not confirmed",
                fallback:
                    "We couldn't confirm your payment. Please don't try again — contact support so we can verify it.",
            };
        default:
            return {
                title: "Payment declined",
                fallback:
                    "Your payment was declined. Please check your card details and try again.",
            };
    }
};
